import React, { useState, useEffect } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, Modal, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, styled, Switch } from '@mui/material';
import { showToast } from '../../utils/toastUtils';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import ActionButton from '../../components/ActionButton';
import { useSelector } from 'react-redux';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'rgba(156, 146, 172, 0.2)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& .MuiTableCell-root': {
    fontSize: '0.9rem',
    padding: theme.spacing(2),
  },
}));

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1000,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 5,
  borderRadius: 2,
};

const FormContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),
  '& .MuiFormControl-root': {
    marginBottom: theme.spacing(2),
  },
}));

const PageBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  padding: '1rem 2rem',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
}));


const SmallTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    fontSize: '1.1rem',
  },
}));

const SmallSelect = styled(Select)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    fontSize: '1.1rem',
  },
}));

const Products = () => {
  const userIdFromRedux = useSelector(state => state.onboarding?.userId);
  const [organizationId, setOrganizationId] = useState(null);
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    productName: '',
    features: '',
    category: '',
    subCategory: '',
    price: '',
    status: '',
    isNewModel: false
  });

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const effectiveUserId = userIdFromRedux || storedUserId;

    if (effectiveUserId) {
      setOrganizationId(effectiveUserId);
      if (!userIdFromRedux) {
        // Dispatch action to update Redux store with the user ID from localStorage
        // You'll need to import useDispatch and the appropriate action creator
        // const dispatch = useDispatch();
        // dispatch(setUserId(effectiveUserId));
      }
    } else {
      showToast('User not found. Please log in again.', 'error');
      // Redirect to login page or handle this case as needed
      // window.location.href = '/login';
    }
  }, [userIdFromRedux]);

  const fetchProducts = async () => {
    if (!organizationId) {
      setError('Organization ID is not available or invalid. Please try logging in again or contact support.');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/products`, {
        params: { organizationId }
      });

      if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
      } else {
        setProducts([]);
      }

      setLoading(false);
      setError(null);
    } catch (err) {
      const errorMessage = `Failed to fetch products for organizationId: ${organizationId}`;
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchProducts();
    } else {
      setLoading(false);
      setError('Please log in to view products.');
    }
  }, [organizationId, API_BASE_URL]);


const handleEditProduct = (productId) => {
  setProducts(prevProducts =>
    prevProducts.map(p =>
      p.productId === productId ? { ...p, isEditing: true } : p
    )
  );
};

const handleSaveEditedProduct = async (product) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/products/${product.productId}`, {
      ...product,
      organizationId
    });

    if (response.data && response.data.success) {
      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.productId === product.productId ? { ...response.data.product, isEditing: false } : p
        )
      );
      showToast('Product updated successfully', 'success');
    } else {
      throw new Error('Invalid response from the server');
    }
  } catch (error) {
    showToast(`Failed to update product: ${error.message}`, 'error');
  }
};

const handleCancelEdit = (productId) => {
  setProducts(prevProducts =>
    prevProducts.map(p =>
      p.productId === productId ? { ...p, isEditing: false } : p
    )
  );
};

  const handleDeleteProduct = async (productId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/products`, {
        params: { organizationId, productId }
      });
      
      if (response.data && response.data.message === 'Product deleted successfully') {
        setProducts(products.filter(p => p.productId !== productId));
        showToast('Product deleted successfully', 'success');
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'An unexpected error occurred';
      showToast(`Failed to delete product: ${errorMessage}`, 'error');
    }
  };

  const handleStatusChange = async (productId, currentStatus) => {
    const newStatus = currentStatus === 'In Stock' ? 'Out of Stock' : 'In Stock';
    try {
      const response = await axios.put(`${API_BASE_URL}/api/products/${productId}`, {
        organizationId,
        status: newStatus
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setProducts(products.map(p => 
          p.productId === productId ? { ...p, status: newStatus } : p
        ));
        showToast('Product status updated successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to update product status');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      showToast(`Failed to update product status: ${errorMessage}`, 'error');
    }
  };


const handleAddItem = () => {
  const newEmptyProduct = {
    productId: 'new-' + Date.now(), // Temporary ID
    productName: '',
    features: '',
    category: '',
    subCategory: '',
    price: '',
    status: 'In Stock',
    isNewRow: true
  };
  setProducts([newEmptyProduct, ...products]);
};

const handleNewProductChange = (productId, field, value) => {
  setProducts(prevProducts =>
    prevProducts.map(product =>
      product.productId === productId ? { ...product, [field]: value } : product
    )
  );
};

const handleSaveNewProduct = async (product) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/products`, {
      ...product,
      organizationId,
      price: parseFloat(product.price)
    });

    if (response.data && response.data.productId) {
      await fetchProducts();
      showToast('Product added successfully', 'success');
    } else {
      throw new Error('Invalid response from the server');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to add product';
    showToast(errorMessage, 'error');
  }
};


const handleCancelNewProduct = (productId) => {
  setProducts(prevProducts => prevProducts.filter(p => p.productId !== productId));
};

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewProduct({
      productName: '',
      features: '',
      category: '',
      subCategory: '',
      price: '',
      status: ''
    });
    setEditingProduct(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewProduct(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!organizationId) {
      showToast('User ID is not available. Please try logging in again or contact support.', 'error');
      return;
    }

    try {
      const productData = {
        ...newProduct,
        organizationId,
        price: parseFloat(newProduct.price)
      };
      let response;
      if (editingProduct) {
        response = await axios.put(`${API_BASE_URL}/api/products/${editingProduct.productId}`, productData);
      } else {
        response = await axios.post(`${API_BASE_URL}/api/products`, productData);
      }
      if (response.data && (response.data.productId || response.data.success)) {
        let updatedProduct;
        if (editingProduct) {
          updatedProduct = { ...editingProduct, ...productData };
        } else {
          updatedProduct = { ...productData, productId: response.data.productId };
        }
        
        setProducts(prevProducts => 
          editingProduct
            ? prevProducts.map(p => p.productId === editingProduct.productId ? updatedProduct : p)
            : [...prevProducts, updatedProduct]
        );
        
        handleCloseModal();
        showToast(response.data.message || (editingProduct ? 'Product updated successfully' : 'Product added successfully'), 'success');
      } else {
        throw new Error('Invalid response from the server');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      showToast(`Failed to ${editingProduct ? 'update' : 'add'} product: ${errorMessage}`, 'error');
    }
  };

  // Call fetchProducts after successful product addition
  useEffect(() => {
    fetchProducts();
  }, []);

  if (!organizationId) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">
          Organization ID is not available. Please try logging in again or contact support.
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Our Products</h1>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
            <ActionButton
              text="Add Product"
              onClick={handleAddItem}
            />
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Features</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sub-category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.productId} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.isNewRow || product.isEditing ? (
                        <input
                          type="text"
                          value={product.productName}
                          onChange={(e) => handleNewProductChange(product.productId, 'productName', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Product Name"
                        />
                      ) : (
                        product.productName
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.isNewRow || product.isEditing ? (
                        <input
                          type="text"
                          value={product.features}
                          onChange={(e) => handleNewProductChange(product.productId, 'features', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Features"
                        />
                      ) : (
                        product.features
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.isNewRow || product.isEditing ? (
                        <input
                          type="text"
                          value={product.category}
                          onChange={(e) => handleNewProductChange(product.productId, 'category', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Category"
                        />
                      ) : (
                        product.category
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.isNewRow || product.isEditing ? (
                        <input
                          type="text"
                          value={product.subCategory}
                          onChange={(e) => handleNewProductChange(product.productId, 'subCategory', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Sub-category"
                        />
                      ) : (
                        product.subCategory
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.isNewRow || product.isEditing ? (
                        <input
                          type="number"
                          value={product.price}
                          onChange={(e) => handleNewProductChange(product.productId, 'price', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Price"
                        />
                      ) : (
                        `$${typeof product.price === 'number' ? product.price.toFixed(2) : 'N/A'}`
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {product.isNewRow || product.isEditing ? (
                        <select
                          value={product.status}
                          onChange={(e) => handleNewProductChange(product.productId, 'status', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="In Stock">In Stock</option>
                          <option value="Out of Stock">Out of Stock</option>
                        </select>
                      ) : (
                        <div className="flex items-center">
                          <Switch
                            checked={product.status === 'In Stock'}
                            onChange={() => handleStatusChange(product.productId, product.status)}
                            color="primary"
                            size="small"
                          />
                          <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                            product.status === 'In Stock' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {product.status}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.isNewRow ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSaveNewProduct(product)}
                            className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50 transition-colors duration-200"
                            title="Save Product"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCancelNewProduct(product.productId)}
                            className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                            title="Cancel"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : product.isEditing ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSaveEditedProduct(product)}
                            className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50 transition-colors duration-200"
                            title="Save Changes"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCancelEdit(product.productId)}
                            className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                            title="Cancel"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleEditProduct(product.productId)}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition-colors duration-200"
                            title="Edit Product"
                          >
                            <EditIcon sx={{ fontSize: '1rem' }} />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product.productId)}
                            className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                            title="Delete Product"
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="product-modal"
          aria-describedby="modal-to-add-or-edit-product"
        >
          <Box sx={modalStyle}>
            <Typography id="product-modal" variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </Typography>
            <form onSubmit={handleSubmit}>
              <FormContainer>
                <TextField
                  fullWidth
                  label="Product Name"
                  name="productName"
                  value={newProduct.productName}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Category"
                  name="category"
                  value={newProduct.category}
                  onChange={handleInputChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Sub-category"
                  name="subCategory"
                  value={newProduct.subCategory}
                  onChange={handleInputChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={newProduct.price}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="status"
                    value={newProduct.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="In Stock">In Stock</MenuItem>
                    <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Features"
                  name="features"
                  value={newProduct.features}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  variant="outlined"
                  sx={{ gridColumn: '1 / -1' }}
                />
              </FormContainer>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <ActionButton
                  text="Cancel"
                  onClick={handleCloseModal}
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <ActionButton
                  text={editingProduct ? 'Update Product' : 'Add Product'}
                  type="submit"
                />
              </Box>
            </form>
          </Box>
        </Modal>
    </div>
  );
};

export default Products;
